interface Props {
  today: string; // YYYY-MM-DD
  dayStats: Record<string, { done: number; partial: number; total: number }>;
  onSelect?: (date: string) => void;
  selected?: string;
}

const DOW_SHORT = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export function CalendarStrip({ today, dayStats, onSelect, selected }: Props) {
  // Build the 7 cells of the week containing `selected` or `today`.
  // Use `selected` date for week framing so when navigating previous/next weeks,
  // the calendar strip updates to reflect that week.
  const referenceDateStr = selected || today;
  const refDate = new Date(`${referenceDateStr}T00:00:00Z`);

  // Day of week: Monday = 1 ... Sunday = 7.
  const dow = ((refDate.getUTCDay() + 6) % 7) + 1; // shift Sunday=0 → 7, then mod so Mon=0
  const mondayDate = new Date(refDate.getTime() - ((dow - 1) * 24 * 3600 * 1000));

  const cells = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(mondayDate.getTime() + i * 24 * 3600 * 1000);
    const iso = d.toISOString().slice(0, 10);
    const stats = dayStats[iso] || { done: 0, partial: 0, total: 0 };
    const completed = stats.total > 0 && stats.done === stats.total;
    const isToday = iso === today;
    const isSelected = iso === selected;
    return {
      iso,
      dayLabel: DOW_SHORT[i]!,
      label: String(d.getUTCDate()),
      completed,
      isToday,
      isSelected,
      stats,
    };
  });

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "0.5rem",
        width: "100%",
      }}
    >
      <div
        className="shukan-calstrip"
        role="listbox"
        aria-label="This week's days"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          gap: "0.25rem",
        }}
      >
        {cells.map((c) => {
          const percent = c.stats.total > 0 ? Math.round((c.stats.done / c.stats.total) * 100) : 0;
          let backgroundImage = "none";
          let backgroundSize = "auto";

          if (c.stats.total > 0) {
            backgroundImage = `linear-gradient(to top, var(--accent) ${percent}%, transparent ${percent}%), radial-gradient(circle, var(--border) 1px, transparent 1px)`;
            backgroundSize = `100% 100%, 4px 4px`;
          }

          const textColor = percent > 50 ? "var(--bg)" : (c.isSelected || c.isToday ? "var(--fg)" : "var(--fg-dim)");

          return (
            <button
              key={c.iso}
              type="button"
              role="option"
              aria-selected={c.isSelected}
              data-today={c.isToday}
              data-completed={c.completed}
              className="shukan-calstrip-day"
              onClick={() => onSelect?.(c.iso)}
              style={{
                borderColor: c.isSelected ? "var(--accent)" : (c.isToday ? "var(--accent)" : "var(--border)"),
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                padding: "0.6rem 0.2rem",
                backgroundImage: backgroundImage,
                backgroundSize: backgroundSize,
                color: textColor,
                transition: "border-color 80ms ease, background 80ms ease, color 80ms ease",
              }}
            >
              <div style={{ fontSize: "0.6rem", color: percent > 50 ? "inherit" : "var(--fg-dim)" }}>{c.dayLabel}</div>
              <div style={{ fontWeight: c.isToday ? 700 : 400, margin: "0.1rem 0", color: "inherit" }}>{c.label}</div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
